class Osmo0908ImageTabs extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.tabs = Array.from(this.querySelectorAll('[data-op0908-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-op0908-panel]'));
    this.mediaIntent = 0;
    this.revealIntent = 0;
    this.tabIntent = 0;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleMotionChange = this.handleMotionChange.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    this.motionQuery.addEventListener('change', this.handleMotionChange);
    this.mediaObserver = new IntersectionObserver(this.handleIntersection, { rootMargin: '160px 0px' });
    this.mediaObserver.observe(this);
    this.activate(Math.max(this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'), 0), false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
    this.motionQuery?.removeEventListener('change', this.handleMotionChange);
    this.mediaObserver?.disconnect();
    this.revealIntent += 1;
    this.tabIntent += 1;
    this.initialized = false;
  }

  handleClick(event) {
    const tab = event.target.closest('[data-op0908-tab]');
    if (!tab || !this.contains(tab)) return;
    this.activate(this.tabs.indexOf(tab), true, true);
  }

  handleKeydown(event) {
    const tab = event.target.closest('[data-op0908-tab]');
    if (!tab || !this.contains(tab)) return;

    const index = this.tabs.indexOf(tab);
    let nextIndex = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    if (nextIndex !== null) {
      event.preventDefault();
      this.activate(nextIndex, true, true);
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.activate(index, false, true);
    }
  }

  handleMotionChange() {
    const activeIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.activate(Math.max(activeIndex, 0), false);
  }

  handleIntersection(entries) {
    this.isNearViewport = entries.some((entry) => entry.isIntersecting);
    if (this.isNearViewport) this.syncActiveMedia();
  }

  activate(index, focus, shouldReveal = false) {
    if (!this.tabs[index] || !this.panels[index]) return;

    const reduceMotion = this.motionQuery.matches;

    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus();
    });

    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === index;
      if (active) this.mountPanelMedia(panel);
      panel.hidden = !active;
      if (!active || reduceMotion) this.pausePanelMedia(panel);
    });

    if (!reduceMotion && this.isNearViewport) this.syncActiveMedia();
    if (shouldReveal) {
      this.revealTab(this.tabs[index]);
      this.revealPanel(this.panels[index]);
    }
  }

  revealTab(tab) {
    const tabIntent = ++this.tabIntent;

    requestAnimationFrame(() => {
      if (tabIntent !== this.tabIntent || !this.isConnected) return;

      const tablist = tab.closest('[role="tablist"]');
      if (!tablist) return;

      const tabBounds = tab.getBoundingClientRect();
      const tablistBounds = tablist.getBoundingClientRect();
      const targetScrollLeft = tablist.scrollLeft
        + tabBounds.left
        - tablistBounds.left
        - ((tablist.clientWidth - tabBounds.width) / 2);

      tablist.scrollTo({
        behavior: this.motionQuery.matches ? 'auto' : 'smooth',
        left: targetScrollLeft,
      });
    });
  }

  revealPanel(panel) {
    const revealIntent = ++this.revealIntent;

    requestAnimationFrame(() => {
      if (revealIntent !== this.revealIntent || !this.isConnected || panel.hidden) return;

      const target = panel.querySelector('[data-op0908-media]') || panel;
      target.scrollIntoView({
        behavior: this.motionQuery.matches ? 'auto' : 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    });
  }

  mountPanelMedia(panel) {
    const template = panel.querySelector('[data-op0908-panel-template]');
    if (!template) return;
    panel.append(template.content.cloneNode(true));
    template.remove();
  }

  pausePanelMedia(panel) {
    this.mediaIntent += 1;
    panel.querySelectorAll('video').forEach((video) => video.pause());
  }

  syncActiveMedia() {
    if (this.motionQuery.matches) return;
    const activePanel = this.panels.find((panel) => !panel.hidden);
    if (!activePanel) return;

    activePanel.querySelectorAll('deferred-media').forEach((media) => {
      const mediaContainer = media.closest('.osmocam-0908-product-images__media-desktop, .osmocam-0908-product-images__media-mobile');
      if (mediaContainer && getComputedStyle(mediaContainer).display === 'none') return;
      if (media.dataset.op0908Started === 'true') return;
      const posterButton = media.querySelector('[ref="deferredMediaPlayButton"]');
      if (!posterButton) return;

      // The shared deferred-media primitive calls video.play() without retaining
      // its Promise. Own playback here so a rapid tab switch is latest-intent
      // safe and cannot surface an unhandled interrupted-play rejection.
      media.querySelector('template')?.content.querySelector('video')?.removeAttribute('autoplay');
      media.dataset.op0908Started = 'true';
      posterButton.click();

      const video = media.querySelector('video');
      if (!video) return;
      const mediaIntent = ++this.mediaIntent;
      video.play().catch(() => {
        if (this.mediaIntent !== mediaIntent) return;
      });
    });
  }
}

if (!customElements.get('osmo-0908-image-tabs')) {
  customElements.define('osmo-0908-image-tabs', Osmo0908ImageTabs);
}
